import LabelWithTooltip from "@/components/form/labels/LabelWithTooltip";
import { InputNumber } from "@/components/InputNumber";
import { selectStandardProps } from "@/utils/componentUtils";
import { validations } from "@/utils/validationUtils";
import { Card, Col, Divider, Form, Input, Row, Select } from "antd";
import { FormInstance } from "antd/lib/form";
import { useTranslation } from "react-i18next";
import { rowGutter } from "../../../../../../../common/constants";
import { ConstructionMaterial, FlatCondition } from "../../../enums";
import { RealtyCalc } from "../../../types";
import FloorPopoverLabel from "../../FloorPopoverLabel";

const colSpan = 4;

interface Props {
  form: FormInstance<RealtyCalc>;
}

export const RealtyCalcApartmentSection = ({ form }: Props) => {
  const { t } = useTranslation();

  const floor = Form.useWatch(["apartmentData", "floor"], form) as number | undefined;

  const handleFloorChange = (floor: string | number | null) => {
    if (floor === 1) {
      const apartmentData = form.getFieldValue("apartmentData");
      form.setFieldsValue({ apartmentData: { ...apartmentData, topFloor: false } });
    }
  };

  return (
    <Card type="inner" className="card-box" title={t("calc.realty.sections.insuredObjectAndAreas")}>
      <Row gutter={rowGutter}>
        <Col span={5}>
          <Form.Item
            name={["apartmentData", "flatCondition"]}
            label={
              <LabelWithTooltip
                tooltip={t("calc.realty.helpers.flatConditionHint")}
                label={t("calc.realty.enums.flatCondition._label")}
              />
            }
            rules={[validations.none]}
          >
            <Select
              {...selectStandardProps}
              allowClear
              options={Object.keys(FlatCondition).map((item) => ({
                value: item,
                label: t("calc.realty.enums.flatCondition." + item),
              }))}
            />
          </Form.Item>
        </Col>

        <Col span={5}>
          <Form.Item
            name={["apartmentData", "constructionMaterial"]}
            label={t("calc.realty.enums.constructionMaterial._label")}
            rules={[validations.notNull]}
          >
            <Select
              {...selectStandardProps}
              options={Object.keys(ConstructionMaterial).map((material) => ({
                value: material,
                label: t("calc.realty.enums.constructionMaterial." + material),
              }))}
            />
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <Form.Item
            name={["apartmentData", "apartmentNumber"]}
            label={t("calc.realty.attrs.apartmentData.apartmentNumber")}
            rules={[validations.notBlank, validations.size(1, 64)]}
          >
            <Input />
          </Form.Item>
        </Col>

        <Col span={5}>
          <Form.Item
            name={["apartmentData", "floor"]}
            label={
              <FloorPopoverLabel
                label={t("calc.realty.attrs.apartmentData.floor")}
                popoverTitle={t("calc.realty.helpers.apartmentFloor")}
              />
            }
            rules={[validations.notNull, validations.minNumber(1)]}
          >
            <InputNumber onChange={handleFloorChange} />
          </Form.Item>
        </Col>

        <Col span={5}>
          <Form.Item
            name={["apartmentData", "numberOfFloors"]}
            label={
              <FloorPopoverLabel
                label={t("calc.realty.attrs.apartmentData.numberOfFloors")}
                popoverTitle={t("calc.realty.helpers.numberOfFloorsDesc")}
                additionalText={t("calc.realty.helpers.numberOfFloorsAdditionalDesc")}
              />
            }
            rules={[validations.none, validations.minNumber(floor)]}
          >
            <InputNumber />
          </Form.Item>
        </Col>
      </Row>

      <Divider className="divider-subheader">{t("calc.realty.sections.areas")}</Divider>

      <Row gutter={rowGutter}>
        <Col span={colSpan}>
          <Form.Item
            name={["apartmentData", "floorArea"]}
            label={t("calc.realty.attrs.apartmentData.floorArea")}
            rules={[validations.notNull, validations.minNumber(1)]}
          >
            <InputNumber addon="area" />
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <Form.Item
            name={["apartmentData", "balconyArea"]}
            label={t("calc.realty.attrs.apartmentData.balconyArea")}
            rules={[validations.none, validations.minNumber(0)]}
          >
            <InputNumber addon="area" />
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <Form.Item
            name={["apartmentData", "terraceArea"]}
            label={t("calc.realty.attrs.apartmentData.terraceArea")}
            rules={[validations.none, validations.minNumber(0)]}
          >
            <InputNumber addon="area" />
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <Form.Item
            name={["apartmentData", "cellarArea"]}
            label={t("calc.realty.attrs.apartmentData.cellarArea")}
            rules={[validations.none, validations.minNumber(0)]}
          >
            <InputNumber addon="area" />
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <Form.Item
            name={["apartmentData", "garageArea"]}
            label={t("calc.realty.attrs.apartmentData.garageArea")}
            rules={[validations.none, validations.minNumber(0)]}
          >
            <InputNumber addon="area" />
          </Form.Item>
        </Col>
      </Row>
    </Card>
  );
};
